import React, { useState } from 'react';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Box
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { setDocumentDirection } from '../utils/documentDirection';

const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸', nativeName: 'Español' },
    { code: 'fr', name: 'French', flag: '🇫🇷', nativeName: 'Français' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳', nativeName: '中文' },
    { code: 'fa', name: 'Farsi', flag: '🇮🇷', nativeName: 'فارسی' }
];

const LanguageSwitcher: React.FC = () => {
    const { i18n, t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLanguageChange = (languageCode: string) => {
        i18n.changeLanguage(languageCode);
        
        // Mark that user has manually selected a language
        // This prevents domain-based auto-switching from overriding user choice
        sessionStorage.setItem('userSelectedLanguage', languageCode);
        
        // Use centralized direction setting function
        setDocumentDirection(languageCode);
        
        handleClose();
    };

    const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

    return (
        <>
            <Tooltip title={t('language.select')}>
                <IconButton
                    onClick={handleClick}
                    size="small"
                    sx={{
                        color: 'inherit',
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                        }
                    }}
                    aria-controls={open ? 'language-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LanguageIcon />
                        <Box component="span" sx={{ fontSize: '1.2rem' }}>
                            {currentLanguage.flag}
                        </Box>
                    </Box>
                </IconButton>
            </Tooltip>
            <Menu
                id="language-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'language-button',
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {languages.map((language) => (
                    <MenuItem
                        key={language.code}
                        onClick={() => handleLanguageChange(language.code)}
                        selected={i18n.language === language.code}
                    >
                        <ListItemIcon sx={{ fontSize: '1.5rem', minWidth: '36px' }}>
                            {language.flag}
                        </ListItemIcon>
                        <ListItemText
                            primary={language.nativeName}
                            secondary={language.name}
                        />
                        {i18n.language === language.code && (
                            <CheckIcon fontSize="small" sx={{ ml: 2, color: 'primary.main' }} />
                        )}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default LanguageSwitcher;

